import { useState, useRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface ObjectUploaderProps {
  maxFileSize?: number; // in bytes, default 5MB
  acceptedTypes?: string; // MIME types, e.g., "image/*"
  onComplete?: (imageUrl: string) => void;
  buttonClassName?: string;
  children: ReactNode;
}

export function ObjectUploader({
  maxFileSize = 5 * 1024 * 1024, // 5MB default
  acceptedTypes = "image/*",
  onComplete,
  buttonClassName,
  children,
}: ObjectUploaderProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxFileSize) {
      toast({
        title: "File Too Large",
        description: `Please select a file smaller than ${Math.round(maxFileSize / (1024 * 1024))}MB.`,
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (acceptedTypes !== "*" && !file.type.match(acceptedTypes.replace("*", ".*"))) {
      toast({
        title: "Invalid File Type",
        description: "Please select an image file (JPG, PNG, GIF, or WebP).",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Step 1: Get presigned upload URL from backend
      const urlResponse = await apiRequest("POST", "/api/objects/upload", {
        contentType: file.type
      });
      if (!urlResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadURL } = await urlResponse.json();

      // Step 2: Upload the file directly to S3 using the presigned URL
      // Important: Include Content-Type header to match the presigned URL signature
      const uploadResponse = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        console.error('[Upload] S3 upload failed:', uploadResponse.status, uploadResponse.statusText);
        throw new Error("Failed to upload file to storage");
      }

      // Step 3: Extract the object key from the presigned URL
      // Presigned URL format: https://bucket.s3.region.amazonaws.com/key?...
      const url = new URL(uploadURL);
      const objectKey = url.pathname.substring(1); // Remove leading slash

      // Construct the internal path that our backend proxy will handle
      const imageUrl = `/api/objects/${objectKey}`;

      toast({
        title: "Upload Complete",
        description: "Your image has been uploaded successfully.",
      });

      onComplete?.(imageUrl);
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "An error occurred while uploading the file.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept={acceptedTypes}
        onChange={handleFileChange}
        className="hidden"
        data-testid="input-file-upload"
      />
      <Button
        onClick={handleClick}
        type="button"
        className={buttonClassName}
        disabled={isUploading}
        data-testid="button-upload"
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="h-4 w-4 mr-2" />
            {children}
          </>
        )}
      </Button>
    </>
  );
}
